import Core       from '@/core/Core';
import Dictionary from '@/core/types/Dictionary';
import Qty        from '@/core/types/Qty';
import CargoUnit  from '@/domains/CargoUnits/CargoUnit';
import CriticalValidationError from '@/core/misc/CriticalValidationError';

/**
 * Паллетоместа ЗТК с корректировками.
 *
 * items — плоский список: паллетоместа документов-позиций (с их id)
 * и корректировки — собственные паллетоместа ЗТК без привязки к документам, qty может быть отрицательным.
 * Корректировок по одному compositeKey может быть сколько угодно: каждая правка в гриде
 * дописывает новую на разницу, существующие юниты не меняются.
 * Факт (то, что показывает и правит грид) — сумма по compositeKey.
 */
export default class CargoUnitsDiff {
  /** Массив, а не Dictionary по compositeKey: юнитов с одним ключом может быть несколько */
  public items: CargoUnit[];

  constructor(items: CargoUnit[] = []) {
    this.items = items;
  }

  /**
   * Паллетоместа, сгруппированные по compositeKey с суммой qty — их показывает и правит грид.
   * Храним записи как пришли с бэка, а группировку отдаёт этот геттер: так видно,
   * что в гриде не сами юниты, а их свёртка.
   * В результат идут копии — исходные юниты не мутируются.
   * Нулевые строки отброшены; отрицательные остаются — их не пропустит валидация бэка.
   */
  get grouped(): Dictionary<number | string, CargoUnit> {
    const result = new Dictionary<number | string, CargoUnit>().useKeyExtractor(unit => unit.compositeKey);

    for (const cargoUnit of this.items) {
      const existing = result.get(cargoUnit.compositeKey);

      if (Core.utils.isNullOrUndefined(existing) === true) {
        result.add(Core.utils.cloneDeep(cargoUnit));
      }
      else {
        existing.qty = existing.qty.plus(cargoUnit.qty);
      }
    }

    for (const key of [...result.keys()]) {
      if (result.get(key).qty.isZero() === true) {
        result.delete(key);
      }
      else {
        // строка остаётся в факте
      }
    }

    return result;
  }

  /**
   * Есть ли корректировки: из всех юнитов вычитаем оригиналы документов (по id).
   * Осталась хоть одна запись — пользователь вносил изменения
   */
  containsDiff(originals: CargoUnit[]): boolean {
    const allIds      = new Set<number>(this.items.map((cargoUnit: CargoUnit) => cargoUnit.id));
    const originalIds = new Set<number>(originals.map((cargoUnit: CargoUnit) => cargoUnit.id));

    return allIds.difference(originalIds).size > 0;
  }

  /** Новое паллетоместо — корректировка как есть; совпал ключ — сложится с остальными в факте */
  add(cargoUnit: CargoUnit): void {
    this.items.push(cargoUnit);
  }

  /** Правка строки факта — дописывает корректировки на разницу */
  update(updatedCargoUnit: CargoUnit, originalCompositeKey: string): void {
    // Строка факта до правки — сумма по исходному ключу
    const originalCargoUnit = this.grouped.get(originalCompositeKey);

    if (originalCompositeKey === updatedCargoUnit.compositeKey) {
      // Ключ тот же — дописываем разницу количества
      this._pushDiff(updatedCargoUnit, updatedCargoUnit.qty.minus(originalCargoUnit.qty));
    }
    else {
      // Ключ сменился (размер, тип, вес) — снимаем строку по старому ключу и добавляем по новому
      this._pushDiff(originalCargoUnit, Qty.zero().minus(originalCargoUnit.qty));
      this._pushDiff(updatedCargoUnit, updatedCargoUnit.qty);
    }
  }

  /**
   * Удаление.
   * - строка факта (CargoUnit) — дописывает корректировку, снимающую её qty целиком;
   * - id — убирает сам юнит документа (позицию удалили из ЗТК). Корректировки остаются:
   *   если они снимали места этой позиции, в факте останется отрицательная строка —
   *   её не пропустит валидация бэка, оператор увидит и поправит.
   */
  delete(cargoUnit: CargoUnit): void;
  delete(id: number): void;
  delete(cargoUnitOrId: CargoUnit | number): void {
    if (typeof cargoUnitOrId === 'number') {
      const index = this.items.findIndex(item => item.id === cargoUnitOrId);

      if (index === -1) {
        throw new CriticalValidationError(`При удалении не получилось найти паллетоместо по id ${cargoUnitOrId}`);
      }
      else {
        this.items.splice(index, 1);
      }
    }
    else if (cargoUnitOrId instanceof CargoUnit) {
      this._pushDiff(cargoUnitOrId, Qty.zero().minus(cargoUnitOrId.qty));
    }
    else {
      throw TypeError('CargoUnitsDiff.delete: нужно передать паллетоместо или его id');
    }
  }

  /** На бэк уходит плоский список, как и раньше */
  toJSON(): CargoUnit[] {
    return this.items;
  }

  /** Дописывает корректировку; нулевую не дописывает — факт не меняется */
  private _pushDiff(template: CargoUnit, qty: Qty): void {
    if (qty.isZero() === false) {
      // Корректировка — новая запись: id шаблона (юнита документа или строки факта) ей не принадлежит
      const diff = Core.utils.cloneDeep(template);
      diff.resetIdentity();
      diff.qty = qty;

      this.items.push(diff);
    }
    else {
      // разницы нет — корректировка не нужна
    }
  }
}